import {
  Lead,
  GuideSection,
  P,
  Term,
  GuideLink,
  Callout,
  GuideTable,
} from './guide-ui';

/** Body for "ClickHouse vs Elasticsearch for logs" comparison guide. */
export function ClickhouseVsElasticsearchGuideBody() {
  return (
    <>
      <Lead>
        Elasticsearch was the default for log analytics for a decade, but at scale
        its storage and cost overhead pushed many teams to look elsewhere, and
        ClickHouse has become the leading answer. The two take fundamentally
        different approaches: an inverted index built for full-text search versus a
        columnar engine built for analytics. This guide compares them for logs.
      </Lead>

      <GuideSection id="two-approaches" title="Two different engines">
        <P>
          <Term>Elasticsearch</Term> is built around an <Term>inverted index</Term>:
          excellent for full-text relevance search, where you want the best-matching
          documents for a term. <Term>ClickHouse</Term> is a{' '}
          <Term>columnar (OLAP) database</Term>: built to scan and aggregate huge
          volumes of data fast. Logs are mostly an analytics workload (filter,
          aggregate, group, trend over time), which is exactly what a columnar engine
          is good at.
        </P>
      </GuideSection>

      <GuideSection id="storage" title="Storage and compression">
        <P>
          This is where the gap is widest. Elasticsearch physically stores each
          document multiple times, in the inverted index for search, in stored
          fields for retrieval, and in doc-values for aggregations, and typically
          achieves only modest compression. ClickHouse, with columnar layout and
          ZSTD compression, commonly compresses log data <Term>5–10×</Term>.
        </P>
        <GuideTable
          head={['', 'Elasticsearch', 'ClickHouse']}
          rows={[
            ['Storage model', 'Inverted index + stored fields + doc-values', 'Columnar with per-column compression'],
            ['Typical compression', '~1.5:1', '~5–10:1 (ZSTD)'],
            ['Relative disk for same data', '~12–19× more', 'Baseline'],
            ['Aggregation query speed', 'Slower at scale', 'Typically 10–50× faster'],
          ]}
        />
      </GuideSection>

      <GuideSection id="cost" title="Cost">
        <P>
          Storage efficiency and CPU usage drive cost. Elasticsearch&apos;s
          index-building and JVM overhead make both storage and compute expensive at
          scale, so the same retention needs far more nodes than ClickHouse. For
          high-volume log workloads, the difference is routinely an order of
          magnitude, which is why log-retention conversations and cost reviews so
          often end up at ClickHouse. See{' '}
          <GuideLink href="/guides/log-retention-requirements">log retention requirements</GuideLink>{' '}
          for the retention side of that trade-off.
        </P>
      </GuideSection>

      <GuideSection id="where-es-wins" title="Where Elasticsearch still wins">
        <P>
          This is not one-sided. Elasticsearch is genuinely strong where you need{' '}
          <Term>full-text relevance ranking</Term>: search-engine use cases, complex
          text scoring, and rich text-analysis features. If your workload is
          document search rather than log analytics, the inverted index is the right
          tool. For logs, high-volume, time-series, filter-and-aggregate, the
          analytics engine usually wins.
        </P>
        <Callout title="Match the engine to the workload">
          <p>
            Logs are an analytics problem far more than a relevance-search problem.
            That is why purpose-built log platforms increasingly run on columnar
            engines rather than inverted indexes.
          </p>
        </Callout>
      </GuideSection>

      <GuideSection id="how-logpulse-helps" title="How LogPulse uses ClickHouse">
        <P>
          LogPulse is built on ClickHouse, which is what makes sub-200ms search across
          billions of events and long, affordable retention possible, without you
          operating the database. A familiar pipe-based query language (LPQL) and
          natural-language AI search sit on top, so you never hand-write SQL. See{' '}
          <GuideLink href="/clickhouse-log-management">log management on ClickHouse</GuideLink>{' '}
          and{' '}
          <GuideLink href="/guides/what-is-log-management">what is log management</GuideLink>.
        </P>
      </GuideSection>
    </>
  );
}
